import React, { JSX } from 'react';
import { IconName } from '@fortawesome/fontawesome-common-types';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CompositeFilterDescriptor, DataResult, SortDescriptor } from '@progress/kendo-data-query';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import {
    GridProps as KendoGridProps,
    GridColumnProps as KendoGridColumnProps,
    GridColumnState,
    GridSelectionChangeEvent,
    GridFilterChangeEvent,
    GridCustomCellProps,
} from '@progress/kendo-react-grid';
import { GridMenuItemProps } from '@components-clean/core/grid/grid-menu/grid-menu-item';
import { IntegrationsConfig } from '@components-clean/core/grid/grid-menu/grid-settings-menu/grid-integrations-menu';
import { GridViewMenuItem } from '@components-clean/core/grid/grid-menu/grid-view-menu';
import { GridStatusProps } from '@components-clean/core/grid/grid-status';

export interface GridColumnProps extends KendoGridColumnProps {
    id?: string; // NOTE: This field is required to enable column reordering functionality
    maxLength?: number;
    lockedByDefault?: boolean;
    show?: boolean;
    showByDefault?: boolean;
    children?: GridColumnProps[];
    activeField?: string;
    minWidth?: number;
    customColumn?: boolean;
    lovData?: string[] | undefined;
    lovFilterContains?: boolean;
    lovMultiSelect?: boolean;
    filterKey?: string;
    comparisonFilterKey?: string;
    inactiveIconTooltip?: string;
    emptyDisplayText?: string;
    ragColumn?: boolean;
    singleValueRagColumn?: boolean;
    lockVisibility?: boolean;
    columnPosition?: number;
    filterDisabled?: boolean;
    filterClassName?: string;
    lockWidth?: boolean;
    showHeaderToolTip?: boolean;
    cellName?: string;
    disableLock?: boolean;
    multiValueTypeColumn?: boolean;
    enhancedGroupedColumnToggling?: boolean;
    evaluationColumn?: boolean;
    hideAdvancedFilter?: boolean;
}

export interface GridState extends DataResult {
    sort?: SortDescriptor[];
    filter?: CompositeFilterDescriptor;
}

export type RowMenuAction = {
    id: number;
    label: string;
    onClick: (dataItem: unknown) => void;
    icon: IconProp;
    disabled?: boolean;
    colorProp?: 'Red';
    rowDisabled?: (dataItem: unknown) => boolean;
    showMenuItem?: (dataItem: unknown) => boolean;
};

export type GridHeaderTitle = {
    id: string | number;
    label: unknown;
};

export interface GridProps extends Omit<KendoGridProps, 'onSelectionChange' | 'onFilterChange'> {
    selectedField?: string;
    title: string | GridHeaderTitle[];
    titleGroupByField?: string;
    onTitleChange?: (e: ComboBoxChangeEvent) => void;
    titleWidth?: number;
    selectedTitle?: GridHeaderTitle | null;
    noTitleMessage?: string;
    testDataId: string;
    columns: GridColumnProps[];
    maxHeight: string;
    totalCount?: number;
    gridViewOptions?: GridViewMenuItem[];
    isExporting?: boolean;
    exportCallback?: () => void;
    exportFilename?: string;
    clearFilter?: () => Promise<boolean | void>;
    addCallback?: () => void;
    onSelectionChange?: (event: GridSelectionChangeEvent, selectedState: { [id: string]: boolean | number[] }) => void;
    preSelectionChange?: (event: GridSelectionChangeEvent) => Promise<boolean>;
    // Whether Row Reordering is enabled for this grid
    rowReorder?: boolean;
    // If rowReorder is true, which data column contains the order index
    orderIndexColumn?: string;
    // A hook into the toggle reorder event. This should return true if an update to the
    // data being used will occur to prevent visible updates (mainly used by the FE grid)
    toggleReorder?: (isActive: boolean) => boolean;
    // A hook into the save reorder event. This should return true if the custom logic is successful (e.g. data save)
    // to fully leave the reorder state
    saveReorder?: (data: unknown[] | DataResult | null | undefined) => boolean;
    // A hook into the cancel reorder event. This should return true to fully leave the reorder state.
    cancelReorder?: () => boolean;
    // A callback to render a custom dialog when toggling the reorder mode. The custom dialog should make sure to
    // call the onSubmit and onClose parameters to correctly toggle the reorder mode.
    reorderConfirmation?: (onSubmit: () => void, onClose: () => void) => Promise<void>;
    // eslint-disable-next-line
    defaultSelectedItem?: Record<string, any>;
    addItemDialog?: (onSubmit: (clearFilter: boolean, clearSort: boolean) => void, onClose: () => void) => JSX.Element;
    canEdit?: boolean;
    loading?: boolean;
    setExportColumns?: (newCols: GridColumnProps[]) => void;
    rowMenuActions?: RowMenuAction[];
    rowMenuActionsWidth?: number;
    excludeHiddenRowMenuActions?: boolean;
    setReorderIsDirty?: () => void;
    setReorderIsActive?: () => void;
    useLoadingOverlay?: boolean;
    clientGrid?: boolean;
    hideAdvancedFilter?: boolean;
    scrollGridRight?: boolean;
    titleReadOnly?: boolean;
    onChipHeaderSelected?: (chipHeader: number) => void;
    selectedChipHeader?: number;
    noPadding?: boolean;
    screenFilterable?: boolean;
    gridSettingsMenuDisabled?: boolean;
    showAddIcon?: boolean;
    showBulkUpdateIcon?: boolean;
    showSendEmailIcon?: boolean;
    gridStatusSetup?: GridStatusProps;
    emailCallback?: (selectedState: { [p: string]: boolean | number[] }) => void;
    bulkUpdateTooltip?: string;
    addRecordTooltip?: string;
    filterListener?: (newFilter?: CompositeFilterDescriptor) => void;
    onBulkUpdateClicked?: () => void;
    isChildGrid?: boolean;
    useFilterWarning?: boolean;
    filterWarningTitle?: string;
    filterWarningBody?: string;
    clearSelectedRows?: () => void;
    clearGridSelectedRows?: boolean;
    apiGrid?: boolean;
    showPlaceholder?: boolean;
    persistentStorageKey?: string;
    gridKey?: number;
    setGridKey?: React.Dispatch<React.SetStateAction<number>>;
    tmpReorder?: DataResult | unknown[] | null | undefined;
    setTmpReorder?: React.Dispatch<React.SetStateAction<DataResult | unknown[] | null | undefined>>;
    selectedState?: { [id: string]: boolean | number[] };
    setSelectedState?: React.Dispatch<React.SetStateAction<{ [id: string]: boolean | number[] }>>;
    currentColumns?: GridColumnProps[];
    setCurrentColumns?: React.Dispatch<React.SetStateAction<GridColumnProps[]>>;
    dataState?: GridState;
    setDataState?: React.Dispatch<React.SetStateAction<GridState>>;
    saveTableState?: React.RefObject<boolean>;
    resetChanges?: () => void;
    setEditMode?: () => void;
    preventAutoscroll?: boolean;
    onFilterChange?: (event: GridFilterChangeEvent) => Promise<boolean | void>;
    gridDownloadDisabled?: boolean;
    apiGridMenuActionItems?: GridMenuItemProps[];
    customGridMenuItems?: GridMenuItemProps[];
    removeCardBorder?: boolean;
    selectionDisabledField?: string;
    suppressAddFilterClearWarning?: boolean;
    externalColumnsState?: GridColumnState[];
    onColumnsStateChange?: (state: GridColumnState[]) => void;
    onStateChange?: (json: string) => void;
    bespokeHeaderActions?: React.ReactNode;
    integrationsOptions?: IntegrationsConfig;
    gridRefresh?: (clearFilters?: boolean, resetSelectionState?: boolean) => void;
}

export const REORDER_COLUMN_ID = 'reorder-column';
export const ORDER_COLUMN_ID = 'order-index-column';
export const ROW_MENU_ACTION_COLUMN_ID = 'row-menu-action-column';

export interface GridRefExtension {
    containerRef: { current: { offsetWidth: number; scrollLeft: number; scrollWidth: number } };
}

export interface IconCustomCellProps extends GridCustomCellProps {
    dataItem: {
        tooltipItems?: string[];
        tooltipItemsCount?: number;
        icon: IconName;
        title: string;
        noItemsMessage: string;
    };
}

export interface ICustomToolTipCellProps extends GridCustomCellProps {
    maxLines?: number;
    dataItem: {
        // The text or list to show in the tooltip, separated by `\n`.
        // If not specified, then the tooltip will not show.
        tooltipItems?: string[] | string;
        // The content to show in the cell.
        content: string;
    };
}
